import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ClockIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  ServerIcon,
  CloudIcon,
  CogIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  BoltIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { monitoringService } from '../services/monitoring';
import type { 
  SystemHealth, 
  WorkspaceStats, 
  ExecutionTrend, 
  AgentMetrics, 
  WorkflowMetrics,
  PerformanceMetrics 
} from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [selectedPeriod, setSelectedPeriod] = useState<'1d' | '7d' | '30d'>('7d');
  const [selectedMetric, setSelectedMetric] = useState<'executions' | 'performance' | 'errors'>('executions');
  
  const workspaceId = currentWorkspace?.id;

  // OpenAI pricing per 1M tokens (as of 2025)
  const PRICING = {
    'gpt-4o': {
      input: 2.0,    // $2.00 per 1M input tokens
      output: 8.0,   // $8.00 per 1M output tokens
    },
    'gpt-4o-mini': {
      input: 0.15,   // $0.15 per 1M input tokens
      output: 0.75,  // $0.75 per 1M output tokens
    },
    'gpt-4': {
      input: 30.0,   // $30.00 per 1M input tokens
      output: 60.0,  // $60.00 per 1M output tokens
    },
    'gpt-3.5-turbo': {
      input: 1.0,    // $1.00 per 1M input tokens
      output: 2.0,   // $2.00 per 1M output tokens
    }
  };

  // Calculate token costs based on actual OpenAI pricing
  const calculateTokenCosts = (metrics: any) => {
    if (!metrics) {
      return {
        totalCost: 0,
        inputCost: 0,
        outputCost: 0,
        reasoningCost: 0,
        cachedSavings: 0,
        breakdown: {
          model: 'gpt-4o-mini',
          inputTokens: 0,
          outputTokens: 0,
          cachedTokens: 0,
          reasoningTokens: 0,
          totalTokens: 0
        }
      };
    }

    // Default to gpt-4o-mini if no model info available
    const defaultModel = 'gpt-4o-mini';
    const pricing = PRICING[defaultModel];
    
    const inputTokens = metrics.total_input_tokens || 0;
    const outputTokens = metrics.total_output_tokens || 0;
    const cachedTokens = metrics.total_cached_tokens || 0;
    const reasoningTokens = metrics.total_reasoning_tokens || 0;

    // Calculate costs (divide by 1M since pricing is per 1M tokens)
    const inputCost = (inputTokens / 1_000_000) * pricing.input;
    const outputCost = (outputTokens / 1_000_000) * pricing.output;
    const reasoningCost = (reasoningTokens / 1_000_000) * pricing.output; // Reasoning tokens priced as output
    
    // Cached tokens save money (50% discount typical)
    const cachedSavings = (cachedTokens / 1_000_000) * pricing.input * 0.5;

    const totalCost = inputCost + outputCost + reasoningCost - cachedSavings;

    return {
      totalCost,
      inputCost,
      outputCost,
      reasoningCost,
      cachedSavings,
      breakdown: {
        model: defaultModel,
        inputTokens,
        outputTokens,
        cachedTokens,
        reasoningTokens,
        totalTokens: inputTokens + outputTokens + reasoningTokens
      }
    };
  };

  // Fetch system health
  const { data: systemHealth, isLoading: isHealthLoading } = useQuery({
    queryKey: ['system-health'],
    queryFn: monitoringService.getSystemHealth,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch workspace metrics
  const { data: workspaceMetrics, isLoading: isMetricsLoading } = useQuery({
    queryKey: ['workspace-metrics', workspaceId],
    queryFn: () => monitoringService.getWorkspaceMetrics(workspaceId!),
    enabled: !!workspaceId,
    refetchInterval: 15000 // Refresh every 15 seconds
  });

  // Cast to enhanced type since backend now returns additional fields
  const enhancedMetrics = workspaceMetrics as WorkspaceStats | undefined;

  // Fetch execution trends
  const { data: executionTrends, isLoading: isTrendsLoading } = useQuery({
    queryKey: ['execution-trends', workspaceId, selectedPeriod],
    queryFn: () => workspaceId ? monitoringService.getExecutionTrends(workspaceId, selectedPeriod === '1d' ? 1 : selectedPeriod === '7d' ? 7 : 30) : Promise.resolve([]),
    enabled: !!workspaceId,
    refetchInterval: 60000 // Refresh every minute
  });

  // Fetch recent executions
  const { data: recentExecutions, isLoading: isExecutionsLoading } = useQuery({
    queryKey: ['recent-executions', workspaceId],
    queryFn: () => workspaceId ? monitoringService.getRecentExecutions(workspaceId, 20) : Promise.resolve({executions: []}),
    enabled: !!workspaceId,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch workspace alerts
  const { data: workspaceAlerts, isLoading: isAlertsLoading } = useQuery({
    queryKey: ['workspace-alerts', workspaceId],
    queryFn: () => workspaceId ? monitoringService.getWorkspaceAlerts(workspaceId) : Promise.resolve({alerts: []}),
    enabled: !!workspaceId,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch agent metrics
  const { data: agentMetrics, isLoading: isAgentMetricsLoading } = useQuery({
    queryKey: ['agent-metrics', workspaceId],
    queryFn: () => workspaceId ? monitoringService.getAgentMetrics(workspaceId, 10) : Promise.resolve([]),
    enabled: !!workspaceId,
    refetchInterval: 60000 // Refresh every minute
  });

  // Fetch workflow metrics
  const { data: workflowMetrics, isLoading: isWorkflowMetricsLoading } = useQuery({
    queryKey: ['workflow-metrics', workspaceId],
    queryFn: () => workspaceId ? monitoringService.getWorkflowMetrics(workspaceId, 10) : Promise.resolve([]),
    enabled: !!workspaceId,
    refetchInterval: 60000 // Refresh every minute
  });

  // Fetch performance metrics
  const { data: performanceMetrics, isLoading: isPerformanceLoading } = useQuery({
    queryKey: ['performance-metrics', workspaceId, selectedPeriod],
    queryFn: () => workspaceId ? monitoringService.getPerformanceMetrics(workspaceId, selectedPeriod) : Promise.resolve(null),
    enabled: !!workspaceId,
    refetchInterval: 60000 // Refresh every minute
  });

  // Helper functions
  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 dark:text-green-400';
      case 'degraded': return 'text-yellow-600 dark:text-yellow-400';
      case 'unhealthy': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon className="h-5 w-5" />;
      case 'degraded': return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'unhealthy': return <XCircleIcon className="h-5 w-5" />;
      default: return <InformationCircleIcon className="h-5 w-5" />;
    }
  };

  // Loading state
  if (isHealthLoading || isMetricsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Dashboard
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Welcome back, {user?.full_name}! Here's your system overview.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(systemHealth?.status || 'unknown')}`}>
                {getStatusIcon(systemHealth?.status || 'unknown')}
                <span className="capitalize">{systemHealth?.status || 'Unknown'}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Performance Metrics Summary - Always show at top */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <ChartBarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceMetrics?.resource_usage?.total_executions?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Executions</p>
            </div>
            <div className="text-center">
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceMetrics?.resource_usage?.avg_execution_time?.toFixed(1) || '0.0'}s
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Execution Time</p>
            </div>
            <div className="text-center">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceMetrics?.resource_usage?.peak_daily_executions?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Peak Daily Executions</p>
            </div>
            <div className="text-center">
              <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceMetrics?.resource_usage?.success_rate?.toFixed(1) || '0.0'}%
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Success Rate</p>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Workspace Metrics Cards - Always show all cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Executions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {enhancedMetrics?.total_executions?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {enhancedMetrics?.success_rate?.toFixed(1) || '0.0'}% success rate
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                <ChartBarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tokens Used</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {enhancedMetrics?.total_tokens_used?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {enhancedMetrics?.total_input_tokens?.toLocaleString() || '0'} input / {enhancedMetrics?.total_output_tokens?.toLocaleString() || '0'} output
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
                <BoltIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${enhancedMetrics?.total_cost_usd?.toFixed(4) || '0.0000'}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {enhancedMetrics?.total_requests?.toLocaleString() || '0'} API requests
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
                <CurrencyDollarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Agents</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {enhancedMetrics?.total_agents?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-orange-600 dark:text-orange-400">
                  {enhancedMetrics?.total_workflows?.toLocaleString() || '0'} workflows
                </p>
              </div>
              <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
                <UsersIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Alerts Section - Only show if there are alerts */}
        {workspaceAlerts && workspaceAlerts.alerts && workspaceAlerts.alerts.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">System Alerts</h3>
            <div className="space-y-3">
              {workspaceAlerts.alerts.map((alert, index) => (
                <div key={index} className={`flex items-center p-3 rounded-lg ${
                  alert.severity === 'critical' ? 'bg-red-50 dark:bg-red-900/20' :
                  alert.severity === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  'bg-blue-50 dark:bg-blue-900/20'
                }`}>
                  <div className="flex-shrink-0">
                    {alert.severity === 'critical' && <ExclamationCircleIcon className="h-5 w-5 text-red-500" />}
                    {alert.severity === 'warning' && <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />}
                    {alert.severity === 'info' && <InformationCircleIcon className="h-5 w-5 text-blue-500" />}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`text-sm font-medium ${
                      alert.severity === 'critical' ? 'text-red-800 dark:text-red-200' :
                      alert.severity === 'warning' ? 'text-yellow-800 dark:text-yellow-200' :
                      'text-blue-800 dark:text-blue-200'
                    }`}>
                      {alert.message}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* OpenAI Agents SDK Usage Analytics - Always show */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Token Usage Breakdown</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Input Tokens</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {enhancedMetrics?.total_input_tokens?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Output Tokens</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {enhancedMetrics?.total_output_tokens?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Cached Tokens</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {enhancedMetrics?.total_cached_tokens?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Reasoning Tokens</span>
                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                  {enhancedMetrics?.total_reasoning_tokens?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Total Tokens</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {enhancedMetrics?.total_tokens_used?.toLocaleString() || '0'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cost Analysis</h3>
            {(() => {
              const costAnalysis = calculateTokenCosts(enhancedMetrics);
              return (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Cost</span>
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                      ${costAnalysis.totalCost.toFixed(4)}
                    </span>
                  </div>
                  
                  <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Input Tokens Cost</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ${costAnalysis.inputCost.toFixed(4)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Output Tokens Cost</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ${costAnalysis.outputCost.toFixed(4)}
                      </span>
                    </div>
                    {costAnalysis.reasoningCost > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Reasoning Tokens Cost</span>
                        <span className="font-medium text-purple-600 dark:text-purple-400">
                          ${costAnalysis.reasoningCost.toFixed(4)}
                        </span>
                      </div>
                    )}
                    {costAnalysis.cachedSavings > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Cached Savings</span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          -${costAnalysis.cachedSavings.toFixed(4)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Model Used</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {costAnalysis.breakdown.model}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Cost per 1M Input</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ${(PRICING as any)[costAnalysis.breakdown.model]?.input || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Cost per 1M Output</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ${(PRICING as any)[costAnalysis.breakdown.model]?.output || 0}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">API Requests</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {enhancedMetrics?.total_requests?.toLocaleString() || '0'}
                    </span>
                  </div>
                  
                  {costAnalysis.cachedSavings > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <p className="text-xs text-green-700 dark:text-green-400">
                        💡 Cached tokens saved ${costAnalysis.cachedSavings.toFixed(4)} in costs
                      </p>
                    </div>
                  )}

                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      💰 Costs calculated using official OpenAI pricing (2025)
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </motion.div>

        {/* Charts and Detailed Metrics with Data - Higher Priority */}
        {(executionTrends && executionTrends.length > 0) || (recentExecutions && recentExecutions.executions && recentExecutions.executions.length > 0) ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          >
            {/* Execution Trends Chart */}
            {executionTrends && executionTrends.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Execution Trends</h3>
                  <div className="flex space-x-2">
                    {(['1d', '7d', '30d'] as const).map((period) => (
                      <button
                        key={period}
                        onClick={() => setSelectedPeriod(period)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          selectedPeriod === period
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
                {isTrendsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="h-48">
                    <div className="space-y-2">
                      {executionTrends.slice(-7).map((trend, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(trend.date).toLocaleDateString()}
                          </span>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-sm text-green-600 dark:text-green-400">
                                {trend.successful_executions}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                              <span className="text-sm text-red-600 dark:text-red-400">
                                {trend.failed_executions}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {trend.total_executions}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recent Executions */}
            {recentExecutions && recentExecutions.executions && recentExecutions.executions.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Executions</h3>
                {isExecutionsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentExecutions.executions.slice(0, 8).map((execution) => (
                      <div key={execution.execution_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-2 h-2 rounded-full ${
                            execution.status === 'completed' ? 'bg-green-500' :
                            execution.status === 'failed' ? 'bg-red-500' :
                            execution.status === 'running' ? 'bg-blue-500' :
                            'bg-yellow-500'
                          }`}></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {execution.workflow_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {execution.steps_completed}/{execution.total_steps} steps
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-900 dark:text-white">
                            {execution.execution_time ? `${execution.execution_time.toFixed(1)}s` : '-'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(execution.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ) : null}

        {/* Agent and Workflow Performance with Data - Higher Priority */}
        {(agentMetrics && agentMetrics.length > 0) || (workflowMetrics && workflowMetrics.length > 0) ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          >
            {/* Agent Performance */}
            {agentMetrics && agentMetrics.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Agents</h3>
                {isAgentMetricsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {agentMetrics.map((agent) => (
                      <div key={agent.agent_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
                            <UsersIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {agent.agent_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {agent.total_executions} executions
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {agent.success_rate.toFixed(1)}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {agent.avg_execution_time.toFixed(1)}s avg
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Workflow Performance */}
            {workflowMetrics && workflowMetrics.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Workflows</h3>
                {isWorkflowMetricsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {workflowMetrics.map((workflow) => (
                      <div key={workflow.workflow_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-green-100 dark:bg-green-900 rounded-full">
                            <CogIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {workflow.workflow_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {workflow.total_executions} executions
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {workflow.success_rate.toFixed(1)}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {workflow.avg_execution_time.toFixed(1)}s avg
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ) : null}

        {/* Last Activity - Show if available */}
        {enhancedMetrics?.last_activity && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Last Activity</h3>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                <ClockIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Last execution: {new Date(enhancedMetrics.last_activity).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {((Date.now() - new Date(enhancedMetrics.last_activity).getTime()) / (1000 * 60)).toFixed(0)} minutes ago
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* BOTTOM SECTION - Zero Value Analytics with Subtle Styling */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8 mt-8">
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">Additional Analytics</h2>
            <p className="text-sm text-gray-500 dark:text-gray-500">These sections will populate as you use the system</p>
          </div>

          {/* Empty Charts Section */}
          {(!executionTrends || executionTrends.length === 0) && (!recentExecutions || !recentExecutions.executions || recentExecutions.executions.length === 0) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
            >
              {/* Empty Execution Trends */}
              {(!executionTrends || executionTrends.length === 0) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6 opacity-60">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400">Execution Trends</h3>
                    <div className="flex space-x-2">
                      {(['1d', '7d', '30d'] as const).map((period) => (
                        <button
                          key={period}
                          onClick={() => setSelectedPeriod(period)}
                          disabled
                          className={`px-3 py-1 text-sm rounded-md transition-colors cursor-not-allowed ${
                            selectedPeriod === period
                              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                          }`}
                        >
                          {period}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                      <ChartBarIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-500">No execution data available</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty Recent Executions */}
              {(!recentExecutions || !recentExecutions.executions || recentExecutions.executions.length === 0) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6 opacity-60">
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-4">Recent Executions</h3>
                  <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                      <ClockIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-500">No recent executions</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Empty Agent and Workflow Performance */}
          {(!agentMetrics || agentMetrics.length === 0) && (!workflowMetrics || workflowMetrics.length === 0) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
            >
              {/* Empty Agent Performance */}
              {(!agentMetrics || agentMetrics.length === 0) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6 opacity-60">
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-4">Top Agents</h3>
                  <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                      <UsersIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-500">No agent data available</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty Workflow Performance */}
              {(!workflowMetrics || workflowMetrics.length === 0) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6 opacity-60">
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-4">Top Workflows</h3>
                  <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                      <CogIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-500">No workflow data available</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Getting Started Actions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6 text-center"
          >
            <div className="p-4 bg-blue-100 dark:bg-blue-900/50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <ArrowTrendingUpIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Get Started</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create agents and run workflows to see comprehensive analytics and performance metrics.
            </p>
            <div className="flex justify-center space-x-4">
              <button 
                onClick={() => window.location.href = '/agents'}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shadow-sm"
              >
                Create Agent
              </button>
              <button 
                onClick={() => window.location.href = '/workflows'}
                className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors shadow-sm"
              >
                Create Workflow
              </button>
            </div>
          </motion.div>
        </div>

        {/* Empty State - Show when no data is available */}
        {(!enhancedMetrics || (enhancedMetrics.total_executions === 0 && enhancedMetrics.total_tokens_used === 0 && enhancedMetrics.total_cost_usd === 0 && enhancedMetrics.total_agents === 0)) && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center"
          >
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <ChartBarIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Data Available</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Start by creating agents and running workflows to see analytics and performance metrics.
            </p>
            <div className="flex justify-center space-x-4">
              <button 
                onClick={() => window.location.href = '/agents'}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Create Agent
              </button>
              <button 
                onClick={() => window.location.href = '/workflows'}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                Create Workflow
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 